import {
  IMessageBarProps as FIMessageBarProps,
  IMessageBarStyles,
  IRawStyle,
  MessageBar as FMessageBar,
  MessageBarType,
} from '@fluentui/react';
import { forwardRef, ReactNode, Ref, useEffect, useState } from 'react';

import { IComponent, IconName, IH2OTheme, IStyleFunctionOrObject } from '../../models/components.models';
import { useClassNames, useTheme, useThemeStyles } from '../../utils/hooks';
import { combineClassNames, singleOrArrayToArray } from '../../utils/utils';
import { Loader } from '../Loader/Loader';
import {
  loaderStylesProgressIndicatorMessageBarError,
  loaderStylesProgressIndicatorMessageBarInfo,
  loaderStylesProgressIndicatorMessageBarSuccess,
  loaderStylesProgressIndicatorMessageBarWarning,
  loaderStylesProgressIndicatorPercentMessageBar,
} from '../Loader/Loader.styles';
import {
  IMessageBarContainerClassnames,
  IMessageBarContainerStyles,
  messageBarContainerStylesDefault,
  messageBarContainerStylesError,
  messageBarContainerStylesInfo,
  messageBarContainerStylesSuccess,
  messageBarContainerStylesWarning,
  messageBarStylesDefault,
} from './MessageBar.styles';
import { IMessageBarContentProps, MessageBarContent } from './MessageBarContent';

export interface IMessageBarProps extends Omit<FIMessageBarProps, 'styles'>, IComponent<Partial<IMessageBarStyles>> {
  title?: string;
  subTitle?: string;
  headerSecondContent?: ReactNode;
  count?: number;
  read?: boolean;
  timeout?: number;
  timeoutPercentComplete?: number;
  hasLargeContentWidth?: boolean; // progress bar etc
  messageBarContentProps?: IMessageBarContentProps;
  messageBarContainerStyles?:
    | IStyleFunctionOrObject<IMessageBarContainerStyles>
    | IStyleFunctionOrObject<IMessageBarContainerStyles>[];
  onTimeoutPercentComplete?: (percentComplete: number) => void;
}

const getComponentStyles = (
  theme: IH2OTheme,
  messageBarType: MessageBarType | undefined,
  iconName?: string
): Partial<IMessageBarStyles> => {
  let styles: Partial<IMessageBarStyles>;
  switch (messageBarType) {
    case MessageBarType.success:
      styles = {
        root: {
          backgroundColor: theme.semanticColors?.messageBarBackgroundSuccess,
        },
        iconContainer: {
          i: {
            color: theme.semanticColors?.messageBarIconSuccess,
          },
        },
        text: {
          color: theme.semanticColors?.messageBarContentTextSuccess,
        },
      };
      break;
    case MessageBarType.info:
      styles = {
        root: {
          backgroundColor: theme.semanticColors?.messageBarBackgroundInfo,
        },
        iconContainer: {
          i: {
            color: theme.semanticColors?.messageBarIconInfo,
          },
        },
        text: {
          color: theme.semanticColors?.messageBarContentTextInfo,
        },
      };
      break;
    case MessageBarType.blocked:
    case MessageBarType.severeWarning:
    case MessageBarType.error:
      styles = {
        root: {
          backgroundColor: theme.semanticColors?.messageBarBackgroundError,
        },
        iconContainer: {
          i: {
            color: theme.semanticColors?.messageBarIconError,
          },
        },
        text: {
          color: theme.semanticColors?.messageBarContentTextError,
        },
      };
      break;
    case MessageBarType.warning:
      styles = {
        root: {
          backgroundColor: theme.semanticColors?.messageBarBackgroundWarning,
        },
        iconContainer: {
          i: {
            color: theme.semanticColors?.messageBarIconWarning,
          },
        },
        text: {
          color: theme.semanticColors?.messageBarContentTextWarning,
        },
      };
      break;
    default:
      styles = {
        root: { backgroundColor: theme.palette?.white },
        iconContainer: iconName ? undefined : { display: 'none' },
        text: {},
      };
      break;
  }
  if (messageBarType !== undefined) {
    styles.text = { ...(styles.text as IRawStyle), marginLeft: 16 };
  }
  return { ...styles };
};

const MessageTypeIconMap = new Map<MessageBarType | undefined, string>([
  [MessageBarType.success, IconName.CompletedSolid],
  [MessageBarType.info, IconName.InfoSolid],
  [MessageBarType.error, IconName.WarningSolid],
  [MessageBarType.severeWarning, IconName.WarningSolid],
  [MessageBarType.blocked, IconName.Blocked2],
  [MessageBarType.warning, IconName.WarningSolid],
]);

const MessageTypeLoaderStylesMap = new Map<MessageBarType | undefined, any>([
  [MessageBarType.success, loaderStylesProgressIndicatorMessageBarSuccess],
  [MessageBarType.info, loaderStylesProgressIndicatorMessageBarInfo],
  [MessageBarType.error, loaderStylesProgressIndicatorMessageBarError],
  [MessageBarType.severeWarning, loaderStylesProgressIndicatorMessageBarWarning],
  [MessageBarType.blocked, loaderStylesProgressIndicatorMessageBarError],
  [MessageBarType.warning, loaderStylesProgressIndicatorMessageBarWarning],
]);

const MessageTypeContainerStylesMap = new Map<MessageBarType | undefined, any>([
  [MessageBarType.success, messageBarContainerStylesSuccess],
  [MessageBarType.info, messageBarContainerStylesInfo],
  [MessageBarType.error, messageBarContainerStylesError],
  [MessageBarType.severeWarning, messageBarContainerStylesWarning],
  [MessageBarType.blocked, messageBarContainerStylesError],
  [MessageBarType.warning, messageBarContainerStylesWarning],
]);

export const MessageBar = forwardRef((props: IMessageBarProps, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    styles,
    hasLargeContentWidth,
    messageBarContentProps,
    title,
    subTitle,
    headerSecondContent,
    messageBarContainerStyles,
    timeout,
    timeoutPercentComplete = 100,
    count,
    read,
    onTimeoutPercentComplete,
    messageBarIconProps,
    ..._fProps
  } = props;
  const { actions, onDismiss, messageBarType, children, onClick } = props;
  const [percentComplete, setPercentComplete] = useState(100);
  const theme = useTheme();
  const fProps: FIMessageBarProps = {
    ..._fProps,
    className: combineClassNames(
      className,
      !!actions && 'has-actions',
      hasLargeContentWidth && 'has-large-content-width',
      !!onDismiss && 'has-dismissal-icon',
      !!messageBarType && `MessageBar-${messageBarType}`,
      read && 'read',
      !!onClick && 'has-click-handler'
    ),
    actions,
    isMultiline: false,
    styles: useThemeStyles<Partial<IMessageBarStyles>>(messageBarStylesDefault, [
      getComponentStyles(theme, messageBarType, messageBarIconProps?.iconName),
      ...singleOrArrayToArray(styles),
    ]),
    messageBarIconProps: {
      ...messageBarIconProps,
      iconName: messageBarIconProps?.iconName || MessageTypeIconMap.get(messageBarType),
    },
    onDismiss,
  };
  const contentProps: IMessageBarContentProps = {
    ...messageBarContentProps,
    messageBarType,
    title,
    subTitle,
    headerSecondContent,
    read,
  };
  const classNames = useClassNames<IMessageBarContainerStyles, IMessageBarContainerClassnames>(
    'MessageBarContainer',
    messageBarContainerStylesDefault,
    [MessageTypeContainerStylesMap.get(messageBarType), ...singleOrArrayToArray(messageBarContainerStyles)],
    className
  );
  useEffect(() => {
    if (timeout) {
      const interval = 100;
      const decreaseRate = (interval / timeout) * 100;
      setPercentComplete(timeoutPercentComplete);
      const id = setInterval(() => {
        setPercentComplete((percentComplete) => {
          const percent = percentComplete - decreaseRate;
          if (onTimeoutPercentComplete) {
            onTimeoutPercentComplete(percent);
          }
          if (percent <= 0) {
            clearInterval(id);
            if (onDismiss) {
              onDismiss();
            }
          }
          return percent;
        });
      }, interval);
      return () => clearInterval(id);
    }
    return;
  }, [timeoutPercentComplete]);
  useEffect(() => {
    setPercentComplete(100);
  }, [count]);

  return (
    <div className={classNames.root} ref={ref} data-test={props['data-test'] || 'message-bar'}>
      <FMessageBar {...fProps}>
        <MessageBarContent {...contentProps}>{children}</MessageBarContent>
      </FMessageBar>
      {timeout ? (
        <div className={classNames.loader}>
          <Loader
            styles={[loaderStylesProgressIndicatorPercentMessageBar, MessageTypeLoaderStylesMap.get(messageBarType)]}
            percentComplete={percentComplete / 100}
          />
        </div>
      ) : null}
      {count && <div className={classNames.count}>{count}</div>}
    </div>
  );
});
